import { Avatar, Icon } from '../common';
import { clipsName, showsChipBox, showsName, type DutyMode } from './dutyFit';
import type { Staff } from '../staff/types';

// The command strip's roster segment (daily-command-strip AC8–AC12).
//
// EVERY ROSTERED HELPER SHOWS THEIR AVATAR, in every state and at every width.
// That is an acceptance criterion, not a preference, and it is why the fit
// ladder in dutyFit.ts sheds the chip box and then the name but never the face.
//
// AM and PM are shift MARKERS, not titles: a short coloured rule then the
// label, teal for the morning and amber for the afternoon. They sit immediately
// beside the segment's own ON DUTY title, and dressed identically the two read
// as one run of small caps with no seam — which is exactly how they were drawn
// before this. The same treatment appears in the week panel, so a shift is
// named the same way whether you are reading today on the rail or Thursday in
// the panel.

export interface DutySegmentProps {
  am: Staff[];
  pm: Staff[];
  mode: DutyMode;
  open: boolean;
  onToggle: () => void;
}

// A helper on the rail is a LABEL, not a control.
//
// It was a button, and a button cannot live inside another button: the whole
// segment is itself a button, so the markup was invalid and — worse — clicking
// the middle of the segment opened the helper editor instead of the week, which
// is where the pointer naturally lands. The visual contract caught it; nothing
// in the typechecker or the unit suite could.
//
// Picking a helper now happens in the WEEK PANEL, where the names are not
// inside a button and there is room to see who you are choosing. That is also
// the better place for it: you pick a person while looking at the week they
// work, not while looking at a 44px rail.
function Helper({ s, mode }: { s: Staff; mode: DutyMode }) {
  const first = s.name.split(' ')[0] ?? s.name;
  return (
    <span className={'cs-helper' + (showsChipBox(mode) ? ' boxed' : '')} title={s.name} aria-label={s.name}>
      <Avatar name={s.name} id={s.id} size={22} />
      {showsName(mode) && (
        <span className={'cs-helper-name' + (clipsName(mode) ? ' is-clipped' : '')}>{first}</span>
      )}
    </span>
  );
}

export function DutySegment({ am, pm, mode, open, onToggle }: DutySegmentProps) {
  const half = (label: string, team: Staff[], period: 'am' | 'pm') => (
    <span className={'cs-half ' + period}>
      <span className={'cs-shift ' + period}>{label}</span>
      <span className={'cs-team' + (showsName(mode) ? '' : ' faces')}>
        {team.length ? (
          team.map((s) => <Helper key={s.id} s={s} mode={mode} />)
        ) : (
          <span className="cs-none">—</span>
        )}
      </span>
    </span>
  );

  return (
    <div className="cs-seg cs-seg-duty">
      <button type="button" className="cs-seg-btn" onClick={onToggle} aria-expanded={open}>
        <span className="cs-seg-ico" aria-hidden="true">
          <Icon name="users" size={14} />
        </span>
        <span className="cs-seg-title">On duty</span>
        {am.length === 0 && pm.length === 0 ? (
          <span className="cs-stack">
            <span className="cs-none">No one rostered</span>
          </span>
        ) : (
          <span className="cs-stack">
            {half('AM', am, 'am')}
            <span className="cs-divider" aria-hidden="true" />
            {half('PM', pm, 'pm')}
          </span>
        )}
        <span className="cs-chev" aria-hidden="true">
          <Icon name="chevron-down" size={13} />
        </span>
      </button>
    </div>
  );
}
